import { FC } from 'react'

import { Box, Button, VStack } from '@chakra-ui/react'
import { CheckCircleIcon, ResetIcon, UndoIcon } from '@editor/assets/icons'
import { MODAL_TYPES } from '@editor/config/constants'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { EditorConfig } from '@editor/interfaces/editor'
import { Tooltip } from '@infraloop/common'
import mixpanel from 'mixpanel-browser'
import { isTablet } from 'react-device-detect'

import { Tools } from '../tools/setup'

interface ToolsPanelProps {
  toolsConfig: (
    | {
        key: string
        auth: boolean
        hook: EditorConfig
      }
    | undefined
  )[]
}

const ToolsPanel = ({ toolsConfig }: ToolsPanelProps) => {
  // Context
  const { showModal } = useGlobalModalContext()

  // Module panels
  const modulePanels = (
    Tools.map((tool) => [
      tool.components?.panel,
      ...(tool.toolbar?.variants?.map((variant) => variant.components?.panel) || []),
    ])
      .flat()
      .filter(Boolean)
      .flat() as ([FC, number] | FC)[]
  ).map<[FC, number]>((panel) => (panel && Array.isArray(panel) ? panel : [panel, Number.MAX_SAFE_INTEGER]))

  // Merge all tools and sort by order
  const toolsPanels = [...modulePanels].sort((a, b) => a[1] - b[1]).map((panel) => panel[0])

  return (
    <VStack w="100%" gap={0} data-testid="tools-panel" className="panel tools-panel">
      {toolsPanels.map((ToolPanel) => (
        <ToolPanel key={`tool-panels-${ToolPanel.name}`} />
      ))}

      <VStack className="tool-buttons">
        {toolsConfig.map(
          (tool) =>
            tool?.auth &&
            tool.hook?.buttons?.undo?.isShown() && (
              <Button
                key={`tool-action-button-undo-${tool.key}`}
                data-testid={`action-button-undo-${tool.key}`}
                variant="panel-dark"
                size={isTablet ? 'lg' : 'md'}
                fontSize="md"
                onClick={() => {
                  document.dispatchEvent(new Event('editor:tool-undo'))
                }}
                disabled={tool.hook.buttons.undo.isDisabled()}
                width="full"
                justifyContent="space-between"
                spinnerPlacement="end"
              >
                {tool.hook.buttons.undo.label || '点をひとつ戻す'}
                <UndoIcon />
              </Button>
            ),
        )}
        {toolsConfig.map(
          (tool) =>
            tool?.auth &&
            tool.hook?.buttons?.reset?.isShown() && (
              <Button
                key={`tool-action-button-reset-${tool.key}`}
                data-testid={`action-button-reset-${tool.key}`}
                variant="panel-dark"
                size={isTablet ? 'lg' : 'md'}
                fontSize="md"
                onClick={() => {
                  showModal({
                    title: `リセット`,
                    body: '変更をリセットしますか？',
                    confirmText: 'リセット',
                    modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
                    onConfirm: () => {
                      tool.hook.buttons!.reset!.onClick()
                      mixpanel.track('Reset tool', {
                        Tool: tool.key,
                        Reason: 'Reset button',
                      })
                      return true
                    },
                  })
                }}
                disabled={tool.hook.buttons.reset.isDisabled()}
                width="full"
                justifyContent="space-between"
                spinnerPlacement="end"
              >
                リセット
                <ResetIcon />
              </Button>
            ),
        )}
        {toolsConfig.map(
          (tool) =>
            tool?.auth &&
            tool.hook?.buttons?.submit?.isShown() && (
              <Tooltip
                key={`tool-action-submit-tooltip-${tool.hook.buttons.submit.key}`}
                label={tool.hook.buttons.submit.tooltip}
                positioning={{ placement: 'left' }}
                showArrow
              >
                <Button
                  data-testid={`action-button-${tool.hook.buttons.submit.key || 'submit'}`}
                  colorPalette="blue"
                  size={isTablet ? 'lg' : 'md'}
                  fontSize="md"
                  onClick={tool.hook.buttons.submit.onClick}
                  loading={tool.hook.buttons.submit.isLoading ? tool.hook.buttons.submit.isLoading() : false}
                  disabled={tool.hook.buttons.submit.isDisabled()}
                  loadingText={tool.hook.buttons.submit.loadingLabel || '保存中'}
                  width="full"
                  justifyContent="space-between"
                  spinnerPlacement="end"
                >
                  {tool.hook.buttons.submit.label}
                  {tool.hook.buttons.submit.icon || <CheckCircleIcon />}
                </Button>
              </Tooltip>
            ),
        )}
      </VStack>
    </VStack>
  )
}

export default ToolsPanel
